/** Shared math, deterministic noise and colour helpers (no platform APIs). */

import { TEAM_DEFS } from './constants';

export const clamp = (v: number, lo: number, hi: number) => (v < lo ? lo : v > hi ? hi : v);
export const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
export const smoothstep = (a: number, b: number, x: number) => {
  const t = clamp((x - a) / (b - a || 1e-6), 0, 1);
  return t * t * (3 - 2 * t);
};

/* ------------------------------------------------------------ determinism */

/** Fast, deterministic, seedable PRNG (mulberry32). */
export function mulberry32(seed: number) {
  let a = seed >>> 0;
  return function () {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Deterministic 2D hash -> [0,1). Identical on server and client. */
export function hash2(x: number, y: number, seed: number): number {
  let h = Math.imul(x | 0, 374761393) + Math.imul(y | 0, 668265263) + Math.imul(seed | 0, 1442695041);
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

/** Smooth value noise on an integer lattice. */
export function valueNoise(x: number, y: number, seed: number): number {
  const xi = Math.floor(x), yi = Math.floor(y);
  const xf = x - xi, yf = y - yi;
  const u = xf * xf * (3 - 2 * xf);
  const v = yf * yf * (3 - 2 * yf);
  const a = hash2(xi, yi, seed);
  const b = hash2(xi + 1, yi, seed);
  const c = hash2(xi, yi + 1, seed);
  const d = hash2(xi + 1, yi + 1, seed);
  return lerp(lerp(a, b, u), lerp(c, d, u), v);
}

/** Fractal brownian motion, normalised to roughly [0,1]. */
export function fbm(x: number, y: number, seed: number, octaves: number): number {
  let amp = 0.5, freq = 1, sum = 0, norm = 0;
  for (let o = 0; o < octaves; o++) {
    sum += valueNoise(x * freq, y * freq, seed + o * 1013) * amp;
    norm += amp;
    amp *= 0.5;
    freq *= 2;
  }
  return sum / norm;
}

/* ------------------------------------------------------------------ colour */

export function hslToRgb(h: number, s: number, l: number): number {
  h = ((h % 360) + 360) % 360 / 360;
  s = clamp(s, 0, 1);
  l = clamp(l, 0, 1);
  if (s === 0) {
    const g = Math.round(l * 255);
    return (g << 16) | (g << 8) | g;
  }
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const hue = (t: number) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  const r = Math.round(hue(h + 1 / 3) * 255);
  const g = Math.round(hue(h) * 255);
  const b = Math.round(hue(h - 1 / 3) * 255);
  return (r << 16) | (g << 8) | b;
}

export const hexString = (rgb: number) => '#' + rgb.toString(16).padStart(6, '0');

/**
 * **The two irrational walks every shade table here is built on.**
 *
 * Multiplying a slot number by an irrational and keeping the fraction gives a
 * sequence that never repeats and stays evenly spread at *every* length — the
 * first three are far apart, and so are the first thirty. Two different
 * irrationals are used so a player's hue and their lightness do not march in
 * step and produce a visible gradient down the team list.
 *
 * This replaced `slot % 5` and `slot % 3`, which looked fine in a table and
 * failed on the map: only five lightnesses and three saturations existed, so
 * the sixth team-mate was an exact repeat of the first and a team of thirteen
 * — an ordinary VS AI side — was five pairs of identical banners. Whose
 * province you were looking at was genuinely unreadable.
 */
const WALK_A = 0.6180339887;      // 1/φ
const WALK_B = 0.4142135624;      // √2 − 1
const frac = (x: number) => x - Math.floor(x);

/**
 * A team-mate's banner: unmistakably the team's colour, their own shade of it.
 *
 * The bands are wide on purpose. A 26° hue spread with a 0.18 lightness range
 * is plenty of separation on a swatch and almost none on a map, where the
 * territory is tinted, small, and sitting next to grass — so hue runs ±22° and
 * lightness across a full 0.30–0.66. It still reads as "the red team" from
 * across the map, and up close every province in it is somebody in particular.
 */
export function playerColor(team: number, slot: number): number {
  const t = TEAM_DEFS[team % TEAM_DEFS.length];
  const hue = t.hue + (frac(slot * WALK_A) - 0.5) * 44;
  const light = 0.30 + frac(slot * WALK_B) * 0.36;
  // two saturations, alternating, so neighbouring slots differ on a third axis
  const sat = slot % 2 === 0 ? 0.84 : 0.62;
  return hslToRgb(hue, sat, light);
}

/**
 * Free-for-all colours. Every player is their own nation, so there is no team
 * hue to share — instead hues are walked by the golden angle, which spreads any
 * number of banners as far apart as they can go and never repeats before it
 * has to. Lightness and saturation cycle on different periods so even close
 * hues stay distinguishable.
 */
export function duelColor(index: number): number {
  const hue = (index * 137.508) % 360;
  const light = 0.44 + (index % 4) * 0.05;
  const sat = 0.78 - (index % 3) * 0.09;
  return hslToRgb(hue, sat, light);
}

export function teamColor(team: number): number {
  const t = TEAM_DEFS[team % TEAM_DEFS.length];
  return hslToRgb(t.hue, 0.72, 0.5);
}

/**
 * Teamless AI kingdoms are slate grey — clearly "not a team", but each one still
 * gets its own readable shade so two neighbouring AI realms never blur together.
 */
export function aiKingdomColor(index: number): number {
  // same two walks as `playerColor`, for the same reason: `index % 5` gave a
  // world of forty AI realms eight identical greys
  const hue = 208 + (frac(index * WALK_A) - 0.5) * 46;
  const light = 0.26 + frac(index * WALK_B) * 0.44;
  return hslToRgb(hue, 0.17, light);
}

/** Monster camps: dark, washed-out greys so they read as wilderness, not a nation. */
export function campColor(index: number, hue: number): number {
  const light = 0.26 + (index % 3) * 0.035;
  return hslToRgb(hue, 0.12, light);
}

export function brighten(rgb: number, f: number): number {
  const r = clamp(((rgb >> 16) & 255) * f, 0, 255) | 0;
  const g = clamp(((rgb >> 8) & 255) * f, 0, 255) | 0;
  const b = clamp((rgb & 255) * f, 0, 255) | 0;
  return (r << 16) | (g << 8) | b;
}

/* ------------------------------------------------------------------- misc */

export function formatTime(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, '0')}`;
}

export function shortNumber(n: number): string {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
  if (n >= 10_000) return Math.round(n / 1000) + 'k';
  if (n >= 1000) return (n / 1000).toFixed(1) + 'k';
  return String(Math.round(n));
}

const NAME_A = ['Aldor', 'Bryn', 'Cael', 'Doran', 'Eryn', 'Fenn', 'Gorm', 'Hal', 'Ivar', 'Jor', 'Kael', 'Lyra', 'Mor', 'Nym', 'Orin', 'Pyra', 'Quill', 'Rha', 'Sith', 'Thal', 'Ulf', 'Vor', 'Wyn', 'Xan', 'Yr', 'Zed'];
const NAME_B = ['gard', 'wyn', 'mir', 'thas', 'dun', 'heim', 'fell', 'rok', 'vale', 'mont', 'crag', 'holt', 'reach', 'spire', 'wick', 'mere'];

export function randomKingdomName(rand: () => number): string {
  const a = NAME_A[Math.floor(rand() * NAME_A.length)];
  const b = NAME_B[Math.floor(rand() * NAME_B.length)];
  return a + b;
}
